import React, { useEffect, useRef, useCallback } from 'react';
import ePub from 'epubjs';

/**
 * Affiche un EPUB (epubjs), flux paginé, sélection via rendition.on('selected').
 *
 * `fileData` est l'ArrayBuffer du livre, téléchargé par l'appelant AVEC l'en-tête
 * d'authentification. On ne passe plus d'URL : epub.js la chargeait lui-même,
 * sans jeton, et recevait 401.
 */
export default function EpubViewer({ fileData, onSelectionChange, className }) {
  const hostRef = useRef(null);
  const bookRef = useRef(null);
  const renditionRef = useRef(null);

  const reportSelection = useCallback(
    (cfiRange, contents) => {
      const book = bookRef.current;
      if (!book || !contents?.document) {
        onSelectionChange?.({ text: '', x: 0, y: 0, pageContext: null });
        return;
      }

      const win = contents.document.defaultView;
      const iframe = win?.frameElement;
      const sel = contents.document.getSelection?.();
      let text = '';
      let rect = { left: 0, top: 0, width: 0, height: 0 };

      if (sel && sel.rangeCount > 0) {
        text = sel.toString().trim();
        try {
          rect = sel.getRangeAt(0).getBoundingClientRect();
        } catch {
          /* ignore */
        }
      }

      if (!text && cfiRange) {
        book
          .getRange(cfiRange)
          .then((range) => {
            if (!range) return;
            const t = range.toString().trim();
            if (!t) {
              onSelectionChange?.({ text: '', x: 0, y: 0, pageContext: null });
              return;
            }
            try {
              rect = range.getBoundingClientRect();
            } catch {
              /* ignore */
            }
            const iframeRect = iframe?.getBoundingClientRect?.() || {
              left: 0,
              top: 0,
            };
            const x = iframeRect.left + rect.left + rect.width / 2;
            const y = iframeRect.top + rect.top;
            onSelectionChange?.({
              text: t,
              x,
              y,
              pageContext: { type: 'epub', cfiRange: String(cfiRange) },
            });
          })
          .catch(() => {
            onSelectionChange?.({ text: '', x: 0, y: 0, pageContext: null });
          });
        return;
      }

      if (!text) {
        onSelectionChange?.({ text: '', x: 0, y: 0, pageContext: null });
        return;
      }

      const iframeRect = iframe?.getBoundingClientRect?.() || { left: 0, top: 0 };
      const x = iframeRect.left + rect.left + rect.width / 2;
      const y = iframeRect.top + rect.top;

      onSelectionChange?.({
        text,
        x,
        y,
        pageContext: { type: 'epub', cfiRange: cfiRange ? String(cfiRange) : null },
      });
    },
    [onSelectionChange],
  );

  useEffect(() => {
    const el = hostRef.current;
    if (!el || !fileData) return undefined;

    const book = ePub(fileData);
    bookRef.current = book;

    let cancelled = false;

    book.ready
      .then(() => {
        if (cancelled || !hostRef.current) return;
        const rendition = book.renderTo(hostRef.current, {
          width: '100%',
          height: '100%',
          flow: 'paginated',
          spread: 'none',
        });
        renditionRef.current = rendition;

        rendition.on('selected', (cfiRange, contents) => {
          reportSelection(cfiRange, contents);
        });
      })
      .catch((err) => {
        console.error('EpubViewer:', err);
      });

    return () => {
      cancelled = true;
      try {
        renditionRef.current?.destroy?.();
      } catch {
        /* ignore */
      }
      renditionRef.current = null;
      try {
        book.destroy?.();
      } catch {
        /* ignore */
      }
      bookRef.current = null;
    };
  }, [fileData, reportSelection]);

  return (
    <div
      className={`epub-viewer-host ${className || ''}`}
      ref={hostRef}
      style={{ width: '100%', height: '100%', minHeight: 0 }}
    />
  );
}
